import { useThemeImage } from "../../hooks/useThemeImage";
import droplet from "../../assets/droplet.png";
import xhsQr from "../../assets/KreneQrcode/xhs.png";
import dyQr from "../../assets/KreneQrcode/dy.png";
import wechatQr from "../../assets/KreneQrcode/wechat.png";
import bilibiliQr from "../../assets/KreneQrcode/bilibili.png";
import {
    createActivityApplication,
    updateActivityApplication,
    getActivityApplications,
    ActivityApplicationItem,
    ActivityApplicationsResponse,
} from "../../api/memberActivities";
import {generateDeviceId} from "../../util/author";
import {getLocalStorage, setLocalStorage} from "../../util/storage";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useMediaQuery} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";
import "./styles.scss";
import "./stylesB.scss";

type Platform = 'xiaohongshu' | 'douyin' | 'wechat' | 'bilibili';

interface PlatformInfo {
    key: Platform;
    name: string;
    handle: string;
    color: string;
    followUrl: string;
    qrImage: string;
    idLabel: string;
    idPlaceholder: string;
    tips: string;
    linkNote?: string;
}

const PLATFORMS: PlatformInfo[] = [
    {
        key: 'douyin',
        name: '抖音',
        handle: '@Krene 创作空间',
        color: 'var(--c-text-primary)',
        followUrl: 'https://www.douyin.com/user/MS4wLjABAAAASYb6YOt4ylWkCpCCv5aBZTJNpcmbLtbZkA1i8wdh5Yc?from_tab_name=main',
        qrImage: dyQr,
        idLabel: '抖音号',
        idPlaceholder: '您的抖音号',
        tips: '如何获取抖音号：进入抖音 - 点击右下角「我」 - 昵称下方会展示抖音号 - 点击抖音号即可复制',
    },
    {
        key: 'xiaohongshu',
        name: '小红书',
        handle: '@Krene 创作空间',
        color: '#FF2442',
        followUrl: 'https://www.xiaohongshu.com/user/profile/6a6993b0000000001400c400',
        qrImage: xhsQr,
        idLabel: '小红书号',
        idPlaceholder: '您的小红书号',
        tips: '如何获取小红书号：进入小红书 - 点击右下角「我」 - 昵称下方会展示小红书号 - 点击小红书号即可复制',
    },
    {
        key: 'bilibili',
        name: 'Bilibili',
        handle: '@Krene创作空间',
        color: '#00A1D6',
        followUrl: 'https://space.bilibili.com/3706924126177319',
        qrImage: bilibiliQr,
        idLabel: 'Bilibili uid',
        idPlaceholder: '您的Bilibili uid',
        tips: '如何获取Bilibili uid：进入Bilibili - 点击右下角「我的」 - 点击右上角「空间」- 点击简介旁边的「详情」- 在IP下方会显示uid - 点击uid即可复制',
    },
    {
        key: 'wechat',
        name: '微信公众号',
        handle: '@Krene 创作空间',
        color: 'var(--c-success)',
        followUrl: 'https://mp.weixin.qq.com/s/p40zc0fpohd0U4bNYLZSpA',
        qrImage: wechatQr,
        idLabel: '微信号',
        idPlaceholder: '您的微信用户名',
        tips: '如何获取微信用户名：进入微信-点击右下角「我」- 微信号上方黑色字即为用户名',
        linkNote: '',
    },
];

const EMPTY_IDS: Record<Platform, string> = {
    xiaohongshu: '',
    douyin: '',
    wechat: '',
    bilibili: '',
};

const BUTTON_STATUS_TEXT: Record<string, string> = {
    pending: '审核中',
    approved: '已通过',
    claimed: '已领取',
};

const extractApplications = (res: ActivityApplicationsResponse | null): ActivityApplicationItem[] => {
    if (!res) return [];
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.items)) return res.data.items;
    return [];
};

const getDeviceId = (): string => {
    let deviceId = getLocalStorage('deviceId');
    if (!deviceId) {
        deviceId = generateDeviceId();
        setLocalStorage('deviceId', deviceId);
    }
    return deviceId;
};

const RULES = [
    '关注 Krene 官方账号：点击右侧【去关注】按钮跳转对应平台，或扫描二维码关注官方账号；',
    '填写信息：按照输入框下方指引，填写您的抖音/小红书/Bilibili的个人 ID 和微信用户名；',
    '填写完成后，点击【提交表单】按钮提交活动申请。',
];

const REWARD_NOTES: Array<[string, string]> = [
    ['审核时效', '工作人员将在 3 个工作日内完成材料核验；'],
    ['奖励发放', '审核通过后，将在消息中心发送通知，可在活动页手动领取奖励；'],
    ['未通过处理', '若审核不通过，可根据驳回原因进行修改并重新提交；'],
    ['参与限制', '每人仅可参与一次活动，若发现冒用他人 ID、重复填写 ID 等情况，平台有权收回奖励。'],
];

const ID_RULES = [
    '四个输入框均为必填，全部完成后提交按钮才会亮起。',
];

export default function NewActiveB() {
    const logo = useThemeImage('logo');
    const { trackFollowActivityCommit, trackFollowAccountClick } = useMatomoTrackingPoint();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:768px)');
    const [ids, setIds] = useState<Record<Platform, string>>(EMPTY_IDS);
    const [missing, setMissing] = useState<Platform[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [submitError, setSubmitError] = useState<string>('');
    const [application, setApplication] = useState<ActivityApplicationItem | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    // 进入页面时查询当前用户的活动申请列表；无记录则先创建一条空申请（content 为空），后续提交走更新
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await getActivityApplications();
            if (cancelled) return;
            let first = extractApplications(res)[0] || null;
            if (!first) {
                const created = await createActivityApplication({
                    type: 'by_id',
                    content: {},
                    deviceId: getDeviceId(),
                });
                if (cancelled) return;
                const newId = (created?.data as {id?: string | number} | undefined)?.id;
                if (created?.success !== false && newId != null) {
                    first = {id: newId, status: 'pending', type: 'by_id', content: {}} as ActivityApplicationItem;
                }
            }
            if (!cancelled) setApplication(first);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // 已有申请类型为 by_screenshot 时应去截图版页面（/active）；by_id 或无记录则留在本页
    useEffect(() => {
        if (application?.type === 'by_screenshot') {
            navigate('/active');
        }
    }, [application, navigate]);

    // 有已提交申请时，用 content 里的 id 预填输入框
    useEffect(() => {
        if (!application?.content) return;
        setIds((prev) => {
            const next = {...prev};
            PLATFORMS.forEach((p) => {
                const v = application.content[p.key];
                if (typeof v === 'string') next[p.key] = v;
            });
            return next;
        });
    }, [application]);

    const isComplete = (platform: Platform) => ids[platform].trim().length > 0;

    const allComplete = PLATFORMS.every((p) => isComplete(p.key));

    // 已有实际提交内容且非「驳回」时才锁定提交按钮（自动创建的空记录不锁定）
    const hasSubmittedContent = Boolean(
        application &&
        application.content &&
        typeof application.content === 'object' &&
        Object.keys(application.content).length > 0
    );
    const isSubmitLocked = Boolean(hasSubmittedContent && application.status !== 'rejected');
    const submitButtonText = isSubmitLocked && application
        ? BUTTON_STATUS_TEXT[application.status] || application.status
        : application?.status === 'rejected'
        ? '重新提交'
        : '提交表单';
    const submitTooltip = application?.status === 'pending'
        ? '当前材料审核中，预计 3 个工作日内完成，请耐心等待审核结果'
        : application?.status === 'approved'
        ? '审核已通过，请在消息中心查看并领取奖励'
        : application?.status === 'claimed'
        ? '奖励已领取'
        : '';
    // 驳回状态下按钮始终高亮可点，不要求先补全材料；点击后由 handleSubmit 校验缺失项
    const submitEnabled = !isSubmitLocked && !submitting && (allComplete || application?.status === 'rejected');

    const handleIdChange = (platform: Platform, value: string) => {
        setIds((prev) => ({...prev, [platform]: value}));
        if (value.trim().length > 0) {
            setMissing((prev) => prev.filter((p) => p !== platform));
        }
    };

    const handleSubmit = async () => {
        const missingList = PLATFORMS.filter((p) => !isComplete(p.key)).map((p) => p.key);
        if (missingList.length > 0) {
            setMissing(missingList);
            return;
        }
        trackFollowActivityCommit({page_id: window.location.href});
        const content: Record<string, string> = {};
        PLATFORMS.forEach((p) => {
            const v = ids[p.key].trim();
            if (v) content[p.key] = v;
        });

        setSubmitting(true);
        setSubmitError('');
        try {
            // 已有申请（id）则走更新接口，否则走创建接口
            const res = application
                ? await updateActivityApplication({
                    id: application.id,
                    type: 'by_id',
                    content,
                })
                : await createActivityApplication({
                    type: 'by_id',
                    content,
                    deviceId: getDeviceId(),
                });
            setSubmitting(false);
            if (res && res.success !== false) {
                setSuccessOpen(true);
                // 提交成功后刷新申请列表
                getActivityApplications().then((listRes) => {
                    setApplication(extractApplications(listRes)[0] || null);
                });
            } else {
                setSubmitError(res?.message || '提交失败，请重试');
            }
        } catch (err) {
            setSubmitting(false);
            setSubmitError('提交失败，请重试');
        }
    };

    const handleSuccessClose = () => {
        setSuccessOpen(false);
        setSubmitError('');
    };

    return (
        <div className="active-page active-page-b">
            <div className="active-panel">
                <div className="active-header">
                    <img className="active-header-logo" src={logo} alt="Krene"/>
                    <div className="active-header-title"></div>
                </div>

                <div className="active-body">
                    {/* 左栏：活动信息 */}
                    <div className="active-left">
                        <div className="active-reward">
                            <img className="active-reward-icon" src={droplet} alt=""/>
                            <div className="active-reward-text">
                                <div className="active-reward-title">🎉 Krene 全平台官号正式启动！</div>
                                <div className="active-reward-amount">
                                    关注各平台官号，即可领取
                                    <span className="active-reward-strong"> 50 </span>
                                    水滴
                                </div>
                            </div>
                        </div>

                        <div className="active-intro">
                            官号将长期分享 AI 生图技巧、精品提示词、专属福利等，欢迎持续关注～
                        </div>

                        {/* 参与方式 */}
                        <div className="active-card">
                            <div className="active-card-title">参与方式</div>
                            <ul className="active-card-list">
                                {RULES.map((rule, index) => (
                                    <li key={index} data-index={index + 1}>{rule}</li>
                                ))}
                            </ul>
                        </div>

                        {/* 奖励说明 */}
                        <div className="active-card">
                            <div className="active-card-title">奖励说明</div>
                            <ul className="active-card-list">
                                {REWARD_NOTES.map(([title, content], index) => (
                                    <li key={index} data-index={index + 1}>
                                        <span>{title}：</span>{content}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 填写 id 规则 */}
                        <div className="active-card">
                            <div className="active-card-title">填写 ID 规则</div>
                            <ul className="active-card-list">
                                {ID_RULES.map((rule, index) => (
                                    <li key={index} data-index={index + 1}>{rule}</li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* 右栏：平台表单 */}
                    <div className="active-right">
                        {application?.status === 'rejected' && (
                            <div className="active-rejected">
                                <div className="active-rejected-title">⚠️ 您的申请未通过审核</div>
                                {application.comment && (
                                    <div className="active-rejected-reason">驳回原因：{application.comment}</div>
                                )}
                                <div className="active-rejected-tip">请核对平台 id 后重新提交</div>
                            </div>
                        )}

                        <div className="active-b-cards">
                            {PLATFORMS.map((p) => {
                                const isMissing = missing.includes(p.key);
                                return (
                                    <div
                                        key={p.key}
                                        className={`active-b-card${isMissing ? ' active-b-card-missing' : ''}`}
                                    >
                                        <div className="active-b-head">
                                            <div className="active-b-head-left">
                                                <span
                                                    className="active-b-badge"
                                                    style={{backgroundColor: p.color}}
                                                >
                                                    {p.name}
                                                </span>
                                                <span className="active-b-scan-hint">{isMobile ? '扫码关注' : '可扫码关注'}</span>
                                            </div>
                                            <div className="active-b-head-right">
                                                <span className="active-b-handle">{p.handle}</span>
                                                <a
                                                    className="active-b-follow"
                                                    href={p.followUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={() => trackFollowAccountClick({page_id: window.location.href, select_content: p.name})}
                                                >
                                                    去关注
                                                </a>
                                            </div>
                                        </div>

                                        <div className="active-b-main">
                                            <div className="active-b-qr">
                                                <img
                                                    src={p.qrImage}
                                                    alt={`${p.name}二维码`}
                                                    style={{cursor: 'pointer'}}
                                                    onClick={() => setPreviewImage(p.qrImage)}
                                                />
                                            </div>

                                            <div className="active-b-body">
                                                {p.linkNote && (
                                                    <div className="active-b-note">{p.linkNote}</div>
                                                )}

                                                <input
                                                    className="active-b-input"
                                                    type="text"
                                                    placeholder={p.idPlaceholder}
                                                    value={ids[p.key]}
                                                    disabled={isSubmitLocked}
                                                    onChange={(e) => handleIdChange(p.key, e.target.value)}
                                                />

                                                <div className="active-b-tips">{p.tips}</div>

                                                {isMissing && (
                                                    <div className="active-b-error">
                                                        请填写{p.idLabel}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="active-b-submit-bar">
                            <Tooltip
                                title={submitTooltip || ''}
                                arrow
                                placement="top"
                            >
                                <div
                                    className="active-submit-btn"
                                    style={{
                                        backgroundColor: submitEnabled ? 'var(--c-brand)' : 'var(--c-border)',
                                        cursor: submitEnabled ? 'pointer' : 'not-allowed',
                                    }}
                                    onClick={() => {
                                        if (submitEnabled) handleSubmit();
                                    }}
                                >
                                    {submitting ? '提交中...' : submitButtonText}
                                </div>
                            </Tooltip>
                            <div className="active-submit-hint">
                                {isSubmitLocked
                                    ? ''
                                    : allComplete
                                    ? '提交后将在 3 个工作日内完成材料核验'
                                    : '四个平台均需填写 id'}
                            </div>
                            {submitError && <div className="active-submit-error">{submitError}</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* 成功弹窗 */}
            {successOpen && (
                <div className="active-mask" onClick={handleSuccessClose}>
                    <div className="active-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="active-dialog-icon">✓</div>
                        <div className="active-dialog-title">活动申请已提交</div>
                        <div className="active-dialog-content">
                            <div>感谢对 Krene 的关注和支持！</div>
                            <div>我们将在 3 个工作日内完成审核，并通过消息中心告知结果。</div>
                            <div>审核通过，即可在活动页领取奖励~</div>
                        </div>
                        <div className="active-dialog-actions">
                            <button className="active-dialog-btn" onClick={handleSuccessClose}>
                            知道了
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 图片预览 Dialog */}
            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                PaperProps={{
                    sx: {maxWidth: 'none', margin: 0, backgroundColor: 'transparent', boxShadow: 'none', overflow: 'hidden'},
                }}
            >
                {previewImage && (
                    <img
                        src={previewImage}
                        alt="预览"
                        style={{
                            maxWidth: '92vw',
                            maxHeight: '92vh',
                            objectFit: 'contain',
                            display: 'block',
                            cursor: 'pointer',
                        }}
                        onClick={() => setPreviewImage(null)}
                    />
                )}
            </Dialog>
        </div>
    );
}
